import { useState, useEffect } from "react";
import { getBookById } from "../firebase/services/bookServices";
import styles from "./TrendingSection.module.css";
import { useRouter } from "@/lib/navigation";
import TrendingSectionSkeleton from "./skeletons/TrendingSectionSkeleton";
import { useAuth } from "../context/AuthContext";

// Keep the default trending books as fallback
const defaultTrendingBooks = [
  {
    id: 1,
    title: "Iron Flame",
    author: "Rebecca Yarros",
    coverUrl: "/images/books/iron-flame.jpg",
    rating: 4.8,
    reviews: 1000,
    position: 1,
  },
  // ... rest of your default books
];

export default function TrendingSection({ content }) {
  const { user } = useAuth();
  const needsRemoteBooks = Boolean(content?.trendingBooks?.length > 0);
  const [books, setBooks] = useState(() =>
    needsRemoteBooks ? [] : defaultTrendingBooks
  );
  const [loading, setLoading] = useState(() => needsRemoteBooks);
  const router = useRouter();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (content?.trendingBooks?.length > 0) {
        try {
          const fetchedBooks = await Promise.all(
            content.trendingBooks.map(async (bookId, index) => {
              const bookData = await getBookById(bookId);
              return {
                ...bookData,
                position: index + 1,
                reviews: bookData.reviewCount || 0,
              };
            })
          );
          if (!cancelled) {
            setBooks(fetchedBooks.slice(0, content?.displayCount || 6));
          }
        } catch (error) {
          console.error("Error fetching trending books:", error);
          if (!cancelled) setBooks(defaultTrendingBooks);
        } finally {
          if (!cancelled) setLoading(false);
        }
      } else {
        setBooks(defaultTrendingBooks);
        setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [content?.trendingBooks, content?.displayCount]);

  const handleReadMore = (id) => {
    router.push(`/books/${id}`);
  };

  // Use content from props if available, otherwise use defaults
  const trendingContent = content || {
    currentContent: "Trending Now",
    displayCount: 6,
    bookDisplay: {
      showRating: true,
      showReviewCount: true,
      showPosition: true,
      showQuickView: true,
    },
  };

  if (loading) {
    return <TrendingSectionSkeleton />;
  }

  return (
    <section className={styles.trendingSection}>
      <div className={styles.categorySection}>
        <div className={styles.categoryHeader}>
          <h2 className={styles.categoryTitle}>
            {trendingContent.currentContent}
          </h2>
          <p className={styles.categorySubtitle}>
            Discover the latest and most popular books
          </p>
        </div>
      </div>
      <div className={styles.trendingGrid}>
        {books.map((book, index) => (
          <div key={book.id} className={styles.trendingCard}>
            <div
              className={styles.imageContainer}
              onClick={() => handleReadMore(book.id)}
            >
              <img
                src={book.coverUrl}
                alt={book.title}
                className={styles.coverImage}
                loading={index === 0 ? "eager" : "lazy"}
                fetchPriority={index === 0 ? "high" : undefined}
                decoding="async"
              />
              <div className={styles.overlay}></div>
              {trendingContent.bookDisplay?.showPosition && (
                <div className={styles.tagWrapper}>
                  <div className={styles.positionBadge}>{book.position}</div>
                </div>
              )}
            </div>
            <div className={styles.bookInfo}>
              <div className={styles.header}>
                <div className={styles.bookTitle}>{book.title}</div>
                <div className={styles.bookAuthor}>by {book.author}</div>
              </div>
              <div className={styles.meta}>
                {trendingContent.bookDisplay?.showRating && !user?.privacySettings?.hideBookRatings && (
                  <div className={styles.rating}>
                    <span className={styles.stars}>★</span>
                    <span className={styles.score}>{book.rating}</span>
                    {trendingContent.bookDisplay?.showReviewCount && (
                      <span className={styles.reviews}>({book.reviews})</span>
                    )}
                  </div>
                )}
                {trendingContent.bookDisplay?.showQuickView && (
                  <button
                    className={styles.readMore}
                    onClick={() => handleReadMore(book.id)}
                  >
                    Read More
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
